import { useState } from "react"
import { DataTypeList, useDataTypesHooks } from "../../../../../features/data-types"
import { Column, Data, FieldType, FieldValue, FormatType, SpinnerDiv, UIPermission, Badge, O } from "../../../../../features/ui"
import { useSystemStateHooks } from "../../../../system"
import { DeliveryRoadmap, Driver, Roadmap } from "../../../state/types"
import { DriverFilters } from "./driverFilters"
import { Operation } from "../../../../operations"
import { Delivery } from "../../../../deliveries"

type Props = {
    drivers: Driver[]
    roadmaps: Roadmap[]
    deliveryRoadmaps: DeliveryRoadmap[]
    deliveries: Delivery[]
    operations: Operation[]
}

export const DriverList = (props: Props) => {
    const { drivers, roadmaps, deliveryRoadmaps, deliveries, operations } = props

    const [rows, setRows] = useState<Data[]>([])
    const [asyncDataLoaded, setAsyncDataLoaded] = useState(false)

    const { userHasPermission } = useSystemStateHooks()
    const readOnly = !userHasPermission(UIPermission.SAVE_DRIVERS)
    const { formatCustomValue } = useDataTypesHooks()

    const setRowsState = (driverList: Driver[], roadmapList: Roadmap[]) => {
        const rowList = driverList.map(driver => {
            const driverRoadmaps = roadmapList.filter(roadmap => roadmap.driverId === driver.id)
            const driverDeliveryRoadmaps = deliveryRoadmaps.filter(deliveryRoadmap => driverRoadmaps.some(roadmap => roadmap.id === deliveryRoadmap.roadmapId))
            const driverDeliveries = deliveries.filter(delivery => driverDeliveryRoadmaps.some(deliveryRoadmap => deliveryRoadmap.deliveryId === delivery.id))
            const operationItems = operations.map(operation => operation.items).flat()

            let transportedValue = 0
            driverDeliveries.forEach(delivery => {
                const itemsValues = delivery.items.map(deliveryItem => {
                    const operationItem = operationItems.find(operationItem => operationItem.id === deliveryItem.itemId)
                    return operationItem ? operationItem.price * deliveryItem.quantity : 0
                })
                transportedValue += itemsValues.reduce((sum, item) => sum + item, 0)
            })
            
            return {
                ...driver,
                transportedValue
            }
        })
        
        setRows(rowList)
        setAsyncDataLoaded(true)
    }

    const columns: Column[] = [
        {
            name: 'name',
            type: FieldType.TEXT,
            label: 'Nombre'
        },
        {
            name: 'identifier',
            type: FieldType.NUMBER,
            label: 'DNI',
            render: (value?: FieldValue) => {
                const identifier = value as O<number>
                return formatCustomValue(identifier, FormatType.DNI) || <Badge label="Vacío" />
            }
        },
        {
            name: 'transportedValue',
            type: FieldType.PRICE,
            label: 'Monto Transportado'
        }
    ]

    const onSearchStart = () => setAsyncDataLoaded(false)

    const onSearchDone = setRowsState

    const renderAsync = (element: JSX.Element) => asyncDataLoaded ? element : <SpinnerDiv />

    return (
        <>
            <DriverFilters
                drivers={drivers}
                roadmaps={roadmaps}
                onSearchStart={onSearchStart}
                onSearchDone={onSearchDone}
            />
            {renderAsync(
                <DataTypeList
                    columns={columns}
                    rows={rows}
                    showDetail={false}
                    readOnly={readOnly}
                />
            )}
        </>
    )
}
